import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import { LoginAPI, setAuthToken, removeAuthToken } from "../api";
import { getUser } from "../../redux/modules/userSlice";
import { useAppDispatch } from "../../redux/hooks";
import AuthLayout from "../Authentication/AuthLayout";
import AuthTextField from "../Authentication/AuthTextField";
import AuthPrimaryButton from "../Authentication/AuthPrimaryButton";
import {
  authActionsSx,
  authFormStackSx,
  authFormTitleSx,
  authLinkButtonSx,
} from "../Authentication/authStyles";

var passwordSchema = Yup.object({
  password: Yup.string()
    .required("Password is required")
    .min(1, "Password is required"),
});

function AdminLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  var email = location.state && location.state.email;
  var role = location.state && location.state.role;
  const [submitting, setSubmitting] = useState(false);

  useEffect(
    function () {
      if (!email) {
        navigate("/login", { replace: true });
      }
    },
    [email, navigate]
  );

  const formik = useFormik({
    initialValues: { password: "" },
    validationSchema: passwordSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: function (values) {
      setSubmitting(true);
      LoginAPI.verifyPassword({ email: email, password: values.password })
        .then(function (response) {
          setSubmitting(false);
          if (response.success && response.token) {
            setAuthToken(response.token);
            dispatch(getUser(response.data));
            toast.success("Login successfully", {
              hideProgressBar: true,
              closeOnClick: true,
            });
            return navigate("/AdminDashboard");
          }

          if (response.success && response.data && response.data.isSuperAdmin) {
            removeAuthToken();
            toast.success("OTP sent successfully", {
              hideProgressBar: true,
              closeOnClick: true,
            });
            return navigate("/otp-verify", { state: { email: email } });
          }

          removeAuthToken();
          formik.setFieldError(
            "password",
            response.message || "Invalid password"
          );
          formik.setFieldTouched("password", true, false);
        })
        .catch(function (err) {
          setSubmitting(false);
          console.log(err);
          var apiMessage =
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
              ? err.response.data.message
              : "Authentication failed";
          formik.setFieldError("password", apiMessage);
          formik.setFieldTouched("password", true, false);
        });
    },
  });

  var passwordError =
    formik.touched.password && formik.errors.password
      ? formik.errors.password
      : "";

  var submitLabel = role === "Admin" ? "Verify Password" : "Get OTP";

  var canSubmit =
    !submitting &&
    Boolean(formik.values.password) &&
    !formik.errors.password;

  if (!email) {
    return null;
  }

  return (
    <AuthLayout>
      <Typography component="h1" sx={authFormTitleSx}>
        LOGIN
      </Typography>

      <Box
        component="form"
        noValidate
        onSubmit={formik.handleSubmit}
        sx={authFormStackSx}
      >
        <AuthTextField
          id="admin-email"
          name="email"
          label="Email"
          type="email"
          value={email}
          disabled
        />

        <AuthTextField
          id="password"
          name="password"
          label="Password"
          type="password"
          autoComplete="current-password"
          autoFocus
          placeholder="Enter Password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(passwordError)}
          helperText={passwordError}
        />

        <Box sx={authActionsSx}>
          <AuthPrimaryButton type="submit" disabled={!canSubmit} loading={submitting}>
            {submitLabel}
          </AuthPrimaryButton>
          <Button
            type="button"
            onClick={function () {
              navigate("/login");
            }}
            sx={authLinkButtonSx}
          >
            Back to Login
          </Button>
        </Box>
      </Box>
    </AuthLayout>
  );
}

export default AdminLogin;
